import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode } from 'react';
import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import type { DocumentSection } from '@shared/types';
import { EditApprovalButton } from '../components/Editable';

/** The design canvas every page is authored against. */
export const PAGE_WIDTH = 1920;
export const PAGE_HEIGHT = 1080;

export const sectionDomId = (sectionId: string): string => `page-${sectionId}`;
/** Lets concurrent read-only renderers avoid duplicating the grid page IDs. */
export const PageDomIdPrefixContext = createContext('');

/**
 * Scales a fixed 1920x1080 design canvas to fit its container via
 * `transform: scale()`, measured with a ResizeObserver. The same component
 * renders grid thumbnails (tiny), gallery pages (large), share and
 * print — only the container width changes.
 *
 * Print gets a belt-and-braces CSS override (see `@media print` in
 * styles.css): the print page box is exactly 13.333in x 7.5in, which at the
 * CSS-standard 96px/in is exactly 1280x720 - precisely 2/3 of 1920x1080 - so
 * print never depends on the ResizeObserver having fired.
 */
export function PageFrame({
  children,
  className,
  onClick,
  ariaLabel,
  ariaPressed,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
  ariaPressed?: boolean;
}): ReactElement {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? el.clientWidth;
      if (width > 0) setScale(width / PAGE_WIDTH);
    });
    observer.observe(el);
    setScale(el.clientWidth > 0 ? el.clientWidth / PAGE_WIDTH : 1);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`page-frame${className ? ` ${className}` : ''}${onClick ? ' page-frame-clickable' : ''}`}
      ref={frameRef}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            ...(ariaLabel ? { 'aria-label': ariaLabel } : null),
            ...(ariaPressed !== undefined ? { 'aria-pressed': ariaPressed } : null),
            onClick,
            onKeyDown: (event: ReactKeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            },
          }
        : null)}
    >
      <div className="page-canvas" style={{ transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Click-to-edit page title, reused by every archetype's PageShell header.
 * Ported from the V2/V3 SectionBlock title editor unchanged. Exported so an
 * archetype with a custom multi-column layout (overview, audience_profiles,
 * geo_strategy) can place the same editable title inside one specific column
 * instead of PageShell's generic full-width header (pair with `hideTitle` on
 * PageShell so the title renders exactly once).
 */
export function PageTitle({
  title,
  readOnly,
  onSave,
  className,
}: {
  title: string;
  readOnly: boolean;
  onSave: (title: string) => void;
  className?: string;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [idleHeight, setIdleHeight] = useState<number | null>(null);
  const idleRef = useRef<HTMLHeadingElement>(null);

  if (editing) {
    const commit = () => {
      setEditing(false);
      const next = draft.trim();
      if (next && next !== title) onSave(next);
    };
    return (
      <h2
        className={`pg-title pg-title-editor${className ? ` ${className}` : ''}`}
        style={idleHeight === null ? undefined : { height: idleHeight }}
      >
        <input
          className="pg-title-input"
          aria-label="Edit page title"
          value={draft}
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(title);
              setEditing(false);
            }
          }}
        />
        <EditApprovalButton onApprove={commit} />
      </h2>
    );
  }

  return (
    <h2
      ref={idleRef}
      className={`pg-title${readOnly ? '' : ' editable-live'}${className ? ` ${className}` : ''}`}
      data-field="title"
      {...(readOnly
        ? null
        : {
            title: 'Click to edit',
            onClick: () => {
              if (window.getSelection()?.isCollapsed === false) return;
              setIdleHeight(idleRef.current?.offsetHeight ?? null);
              setDraft(title);
              setEditing(true);
            },
          })}
    >
      {title}
    </h2>
  );
}

/**
 * The wrapper every archetype renders inside: the anchor the section rail /
 * chat context needs, the data attributes the selection-context reader
 * needs, and the optional title header.
 */
export function PageShell({
  section,
  readOnly,
  onTitleSave,
  children,
  eyebrow,
  hideTitle,
  className,
  band,
  titleClassName,
  style,
}: {
  section: DocumentSection;
  readOnly: boolean;
  onTitleSave: (title: string) => void;
  children: ReactNode;
  eyebrow?: ReactNode;
  hideTitle?: boolean;
  className?: string;
  /** Full-bleed divider band (theme primary) behind the eyebrow + title. */
  band?: boolean;
  titleClassName?: string;
  /**
   * Page knobs (docs/composer-cards.md §13f): CSS custom properties for the
   * one knob better expressed as a variable than a class switch (see
   * `momentum-v3/knobs.ts` `knobVars`) - applied to the same root the
   * `knob-*` classes land on, so a page's CSS can read either.
   */
  style?: CSSProperties;
}): ReactElement {
  const domIdPrefix = useContext(PageDomIdPrefixContext);
  const header =
    eyebrow || !hideTitle ? (
      <div className="pg-header">
        {eyebrow ? <div className="pg-eyebrow">{eyebrow}</div> : null}
        {hideTitle ? null : (
          <PageTitle
            title={section.title}
            readOnly={readOnly}
            onSave={onTitleSave}
            {...(titleClassName ? { className: titleClassName } : null)}
          />
        )}
      </div>
    ) : null;

  return (
    <section
      id={`${domIdPrefix}${sectionDomId(section.id)}`}
      className={`pg pg-${section.type}${band ? ' pg-band' : ''}${className ? ` ${className}` : ''}`}
      data-section-id={section.id}
      data-section-title={section.title}
      style={style}
    >
      {header}
      {children}
    </section>
  );
}
